import React, { useCallback } from "react";
import CellInput, { CellTextarea } from "../../global/CellInput";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faCopy, faTrash } from "@fortawesome/free-solid-svg-icons";
import func from "../../../services/functions";
import bigDecimal from "js-big-decimal";
import { bankStatusStyle, generateStatusStyle, openBankingStatus, bankStatus as bankStatusObj } from "../OpenBankingUtils";
import { useDuplicatePaymentOrder, useGetPaymentOrderById, useSavePaymentOrder } from "./PaymentOrderService";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppContext } from "../../../AppContext";

const moment = require("moment");

export const useGeneratePaymentOrderColumns = ({ t, i18n, getFilteredResults, tableRows, filters, setFilters, refreshStatistics } = {}) => {
    const history = useHistory();
    const { t: tTranslation } = useTranslation("translations");
    const { user: [user] } = useAppContext();
    const userCanDuplicate = user.role.id !== 7;
    const [deleteLoading, setDeleteLoading] = React.useState(false);
    const { duplicatePaymentOrder, loading: duplicateLoading } = useDuplicatePaymentOrder({
        onSuccess: (paymentOrder) => {
            refreshStatistics();
            history.push("/edit-payment-order/" + paymentOrder.id);
        }
    });

    const { getPaymentOrder } = useGetPaymentOrderById();
    const { savePaymentOrderFn } = useSavePaymentOrder();

    const deletePaymentOrder = async (paymentOrderId) => {
        setDeleteLoading(true);
        const paymentOrder = await getPaymentOrder(paymentOrderId);
        savePaymentOrderFn({
            paymentOrder,
            onSuccess: () => {
                setDeleteLoading(false);
                getFilteredResults({ pageSize: tableRows?.rowsPerPage, page: tableRows?.currentPage });
                refreshStatistics();
            },
            actionStatus: { action: "DELETE" }
        });
    };
    const currencyOptions = [
        { value: "ALL", label: "Lek" },
        { value: "EUR", label: "Euro" },
        { value: "USD", label: "USD" },
    ];


    const getStatus = useCallback((order)=>generateStatusStyle(order?.workflowStatus?.name, t),[t]);
    const getBankStatus = useCallback((order)=>bankStatusStyle(order?.bankStatus?.mappedStatusType?.id, t),[t]);

    return [
        {
            header: t(["openBanking.paymentOrderId"]),
            field: "endToEndId",
            style: { minWidth: "250px", maxWidth: "250px" },
            body: (order) => <CellInput value={order?.endToEndId ?? ""} width={230} onFocus={(event) => event.target.select()} />,
            filter: true,
            default: true,
            mobile: { priority: 1, isTitle: true, group: "identity" },
        },
        {
            header: t(["openBanking.registeredDate"]),
            field: "recordDate",
            body: (order) => order?.recordDate ? moment(order?.recordDate).format("DD/MM/YYYY HH:mm") : "",
            style: { minWidth: 200, maxWidth: 200 },
            filter: true,
            filterType: "DATE",
            filterConfig: {
                like: false,
            },
            sortable: true,
            default: true,
            formattedExportValue: true,
            preventExcelFormat: true,
            mobile: { priority: 1, group: "dates" },
        },
        {
            header: t(["openBanking.actions"]),
            style: { minWidth: "240px", maxWidth: "240px" },
            field: "actions",
            body: (order) => (
                <div className='d-flex align-items-center'>
                    <Link
                        to={"/edit-payment-order/" + order.id}
                        style={{ color: "#0275d8" }}
                        className={"nav-link p-0 d-flex align-items-center mr-2 text-primary"}
                    >
                        <FontAwesomeIcon icon={faEye} className="mx-1" />
                        {t("common.view")}
                    </Link>
                    {order?.bankStatus?.status !== 'ERROR' && userCanDuplicate && <a
                        href="#"
                        className={`${duplicateLoading || deleteLoading ? "disabled" : ""} nav-link p-0 d-flex align-items-center ml-2`}
                        onClick={(e) => {
                            e.preventDefault();
                            if (!duplicateLoading && !deleteLoading) {
                                duplicatePaymentOrder(order?.id?.toString());
                            }
                        }}>
                        <FontAwesomeIcon className="mr-1" icon={faCopy} />
                        {t("invoice.duplicate")}
                    </a>
                    }

                    {order?.workflowStatus?.name === "DRAFT"  &&  <a
                        href="#"
                        className={`${deleteLoading || duplicateLoading ? "disabled" : ""} nav-link p-0 d-flex align-items-center text-danger ml-1`}
                        onClick={(e) => {
                            e.preventDefault();
                            if (!deleteLoading && !duplicateLoading) {
                                deletePaymentOrder(order.id?.toString());
                            }
                        }}>
                        <FontAwesomeIcon icon={faTrash} className="mr-1" />
                        {t('common.delete')}

                    </a>
                    }
                </div>
            ),
            filter: false,
            default: true,
            persisted: true,
            mobile: { priority: "action", group: "actions" },
        },
        {
            header: t(["openBanking.description"]),
            field: "description",
            body: transaction => <CellTextarea width={180} value={transaction.description ?? ""} onFocus={(event) => event.target.select()} />,
            style: { minWidth: "200px", maxWidth: "200px" },
            filter: true,
            mobile: { priority: 2 },
        },
        {
            header: t(["openBanking.profiscStatus"]),
            field: "workflowStatus.name",
            style: { minWidth: "180px", maxWidth: "180px" },
            filter: false,
            default: true,
            mobile: { priority: 1, isBadge: true, group: "status" },
            format:(order)=>getStatus(order).status,
            body: (order) => {
                const status = getStatus(order);
                return (
                    <h4 className="mb-0">
                        <small className={`badge badge-${status.badgeClass} ml-2`}>
                            {status?.status}
                        </small>
                    </h4>
                );
            },
            // filterType: "DROPDOWN",
            // filterConfig: {
            //     options: profiscStatusOptions,
            // },
        },
        {
            header: t(["openBanking.transactionNo"]),
            field: "referenceNo",
            style: { minWidth: "200px", maxWidth: "200px" },
            filter: true,
            default: true,
            mobile: { priority: 2 },
            body: (order) => <CellInput value={order?.referenceNo ?? ""} width={180} onFocus={(event) => event.target.select()} />,
        },
        {
            header: t(["openBanking.transactionDate"]),
            field: "transactionDate",
            body: (order) => order?.transactionDate ? moment(order?.transactionDate).format("DD/MM/YYYY HH:mm") : "",
            style: { minWidth: 200, maxWidth: 200 },
            filter: true,
            filterType: "DATE",
            filterConfig: {
                like: false,
            },
            default: true,
            sortable: true,
            formattedExportValue: true,
            preventExcelFormat: true,
            mobile: { priority: 2 },
        },
        {
            header: t(["openBanking.bankStatus"]),
            field: "bankStatus",
            style: { minWidth: "180px", maxWidth: "180px" },
            filter: false,
            default: true,
            sortable: true,
            mobile: { priority: 1, isBadge: true, group: "status" },
            format:(order)=>getBankStatus(order).status,
            body: (order) => {
                const bankStatusObj = getBankStatus(order);
                return (
                    <h4 className="mb-0">
                        <small className={`badge badge-${bankStatusObj.badgeClass} ml-2`}>
                            {bankStatusObj?.status}
                        </small>
                    </h4>
                );
            },
            // filterType: "DROPDOWN",
            // filterConfig: {
            //     options: bankStatusOptions,
            //     optionValue: "value",
            //     optionLabel: "label",
            // },
        },
        {
            header: t(["payRoll.originalBankStatus"]),
            field: "bankStatus.originalBankStatus",
            style: { minWidth: "200px", maxWidth: "200px" },
            filter: false,
            default: true,
            persisted: true,
            mobile: { priority: 3 },
        },
        {
            header: t(["openBanking.executionDate"]),
            field: "requestedExecutionDate",
            body: (order) => order?.requestedExecutionDate ? moment(order?.requestedExecutionDate).format("DD/MM/YYYY HH:mm") : "",
            style: { minWidth: 200, maxWidth: 200 },
            filter: true,
            filterType: "DATE",
            filterConfig: {
                like: false,
            },
            sortable: true,
            formattedExportValue: true,
            preventExcelFormat: true,
            mobile: { priority: 2 },
        },
        {
            header: t(["openBanking.bank"]),
            field: "buyerBankAccountTransactional.buyerBankName",
            style: { minWidth: "200px", maxWidth: "200px" },
            format:(order)=>order?.buyerBankAccountTransactional?.buyerBankName ?? "",
            filterConfig:{
                like: false,
                splitKey:false,
            },
            filter: true,
            default: true,
            mobile: { priority: 1, group: "identity" },
        },
        {
            header: t(["openBanking.buyerIban"]),
            field: "buyerBankAccountTransactional.buyerIban",
            style: { minWidth: "250px", maxWidth: "250px" },
            filterConfig:{
                like: false,
                splitKey:false
            },
            format:(order)=>order?.buyerBankAccountTransactional?.buyerIban ?? "",
            filter: true,
            default: true,
            mobile: { priority: 3 },
            body: (order) => <CellInput value={order?.buyerBankAccountTransactional?.buyerIban ?? ""} width={230} onFocus={(event) => event.target.select()} />,
        },
        {
            header: t(["openBanking.accountNo"]),
            field: "buyerBankAccountTransactional.buyerAccountReference",
            filterConfig:{
                like: false,
                splitKey:false
            },
            style: { minWidth: "200px", maxWidth: "240px" },
            filter: true,
            mobile: { priority: 3 },
            format:(order)=>order?.buyerBankAccountTransactional?.buyerAccountReference ?? "",
            body: (order) => <CellInput value={order?.buyerBankAccountTransactional?.buyerAccountReference ?? ""} width={230} onFocus={(event) => event.target.select()} />,

        },
        {
            header: t(["openBanking.amount"]),
            field: "amount",
            style: { minWidth: "150px", maxWidth: "150px" },
            sortable: true,
            default: true,
            mobile: { priority: 1, group: "financial" },
            body: (order) => order?.amount ? func.getFormattedMoneyValue(order.amount) : "",
        },
        {
            header: t(["openBanking.currency"]),
            field: "currency",
            default: true,
            style: { minWidth: "120px", maxWidth: "120px" },
            body: (order) => order?.currency ?? "",
            filter: true,
            filterType: "MULTISELECT",
            filterConfig: {
                options: currencyOptions,
            },
            mobile: { priority: 2 },
        },
        {
            header: t(["openBanking.exchangeRate"]),
            field: "exchangeRate",
            default: true,
            style: { minWidth: "150px", maxWidth: "150px" },
            body: (order) => order?.exchangeRate ?? "",
            mobile: { priority: 3 },
        },
        {
            header: t(["openBanking.vendorName2"]),
            field: "sellerBankAccountTransactional.sellerName",
            format:(order)=>order?.sellerBankAccountTransactional?.sellerName ?? "",
            filterConfig:{
                like: false,
                splitKey:false
            },
            style: { minWidth: "200px", maxWidth: "200px" },
            filter: true,
            default: true,
            mobile: { priority: 1, group: "identity" },
        },
        {
            header: t(["openBanking.vendorBankName"]),
            field: "sellerBankAccountTransactional.creditorBankName",
            format:(order)=>order?.sellerBankAccountTransactional?.creditorBankName ?? "",
            filterConfig:{
                like: false,
                splitKey:false
            },
            style: { minWidth: "200px", maxWidth: "200px" },
            filter: true,
            default: true,
            mobile: { priority: 2 },
        },
        {
            header: t(["openBanking.accountReference"]),
            field: "sellerBankAccountTransactional.sellerIban",
            format:(order)=>order?.sellerBankAccountTransactional?.sellerIban ?? "",
            filterConfig:{
                like: false,
                splitKey:false
            },
            style: { minWidth: "250px", maxWidth: "250px" },
            filter: true,
            default: true,
            mobile: { priority: 3 },
        },
        {
            header: t(["openBanking.accountReferenceType"]),
            field: "sellerBankAccountTransactional.creditorAccountReferenceType",
            format:(order)=>order?.sellerBankAccountTransactional?.creditorAccountReferenceType ?? "",
            filterConfig:{
                like: false,
                splitKey:false
            },
            style: { minWidth: "220px", maxWidth: "220px" },
            filter: true,
            mobile: { priority: 3 },
        },
        {
            header: t(["openBanking.swift"]),
            field: "sellerBankAccountTransactional.creditorAgent",
            format:(order)=>order?.sellerBankAccountTransactional?.creditorAgent ?? "",
            filterConfig:{
                like: false,
                splitKey:false
            },
            style: { minWidth: "150px", maxWidth: "150px" },
            filter: true,
            mobile: { priority: 3 },
        },
        {
            header: "Creditor Agent Type",
            field: "sellerBankAccountTransactional.creditorAgentType",
            format:(order)=>order?.sellerBankAccountTransactional?.creditorAgentType ?? "",
            filterConfig:{
                like: false,
                splitKey:false
            },
            style: { minWidth: "200px", maxWidth: "200px" },
            filter: true,
            mobile: { priority: 3 },
        },
        {
            header: t(["openBanking.recordUser"]),
            field: "recordUser",
            style: { minWidth: "150px", maxWidth: "150px" },
            filter: true,
            default: true,
            mobile: { priority: 3 },
        },
        {
            header: t(["openBanking.approvalUser"]),
            field: "approvalUser",
            style: { minWidth: "180px", maxWidth: "180px" },
            filter: true,
            mobile: { priority: 3 },
        },
    ];
};

export const generateSubGridInvoiceColumns = ({ t,paymentOrder } = {}) => {
    return [
        {
            header: t(["openBanking.invoiceNo"]),
            field: "docNumber",
            style: { minWidth: "180px", maxWidth: "180px" },
            body: (invoice) => <CellInput value={invoice?.docNumber ?? ""} width={160} onFocus={(event) => event.target.select()} />,
            filter: true,
            sortable: true,
            default: true,
        },
        {
            header: t(["openBanking.fic"]),
            field: "eInvoiceFic",
            style: { minWidth: "280px", maxWidth: "280px" },
            body: (invoice) => <CellInput value={invoice?.eInvoiceFic ?? ""} width={260} onFocus={(event) => event.target.select()} />,
            filter: true,
            default: true,
        },
        {
            header: t(["openBanking.profiscStatus"]),
            field: "purchaseWorkflowStatus",
            style: { minWidth: "180px", maxWidth: "180px" },
            sortable: true,
            default: true,
        },
        {
            header: t(["openBanking.cisStatus"]),
            field: "cisStatus",
            style: { minWidth: "150px", maxWidth: "150px" },
            default: true,
        },
        {
            field: "dueDateTime",
            header: t("paymentOrder.dueDatePayOrder"),
            style: {minWidth:"180px",maxWidth:"300px"},
            body: invoice => invoice.dueDateTime ? moment(invoice.dueDateTime).format("DD/MM/YYYY") : "",
        },
        {
            header: t(["openBanking.currency"]),
            field: "eInvoiceCurrency",
            body:()=>paymentOrder?.currency,
            style: { minWidth: "120px", maxWidth: "120px" },
        },
        {
            header: t(["openBanking.exchangeRate"]),
            field: "exRate",
            style: { minWidth: "150px", maxWidth: "150px" },
        },
        {
            header: t(["openBanking.invoiceAmount"]),
            field: "eInvoiceAmount",
            style: { minWidth: "150px", maxWidth: "150px" },
            default: true,
            body: (invoice) => invoice?.eInvoiceAmount ? func.getFormattedMoneyValue(invoice.eInvoiceAmount) : "",
        },
        {
            header: t(["openBanking.paidAmount"]),
            field: "buyerPaidAmount",
            style: { minWidth: "150px", maxWidth: "150px" },
            default: true,
            body: (invoice) => invoice?.buyerPaidAmount ? func.getFormattedMoneyValue(invoice.buyerPaidAmount) : "",
        },
        {
            header: t(["openBanking.openAmount"]),
            field: "openAmount",
            style: { minWidth: 200, maxWidth: 200 },
            default: true,
            body:(invoice)=> invoice.buyerPaidAmount && invoice.eInvoiceAmount ?  func.getFormattedMoneyValue(bigDecimal.subtract(invoice.eInvoiceAmount,invoice.buyerPaidAmount)) : "0.00",
        },
        {
            header: t(["openBanking.reconcileDate"]),
            field: "eInvoiceDateTime",
            body: (invoice) => invoice?.eInvoiceDateTime ? moment(invoice?.eInvoiceDateTime).format("DD/MM/YYYY HH:mm") : "",
            style: { minWidth: 200, maxWidth: 200 },
            formattedExportValue: true,
            preventExcelFormat: true,
        },
        {
            header: t(["openBanking.reconcilePaymentStatus"]),
            field: "paymentReconcileStatus",
            style: { minWidth: 200, maxWidth: 300 },
        },
    ];
};

export default useGeneratePaymentOrderColumns;
